import {
    createMemoryStorageAdapter,
    type StorageAdapter,
} from "@rosette/ts/src/util/storage.js";


const fallbackStorage = createMemoryStorageAdapter();

function readWebStorage(key: string, storage: Storage | undefined): string | null {
    try {
        if (!storage)
            return null;

        return storage.getItem(key);
    } catch (_) {
        return null;
    }
}

function writeWebStorage(
    key: string,
    value: string | null,
    storage: Storage | undefined,
): boolean {
    try {
        if (!storage)
            return false;

        if (value !== null) {
            storage.setItem(key, value);
        } else {
            storage.removeItem(key);
        }
        return true;
    } catch (_) {
        return false;
    }
}

export const browserStorageAdapter: StorageAdapter = {
    readSession(key: string): string | null {
        const value = readWebStorage(
            key,
            typeof sessionStorage === "undefined" ? undefined : sessionStorage,
        );
        return (value !== null ? value : fallbackStorage.readSession(key));
    },
    writeSession(key: string, value: string | null): boolean {
        const wroteBrowser = writeWebStorage(
            key,
            value,
            typeof sessionStorage === "undefined" ? undefined : sessionStorage,
        );
        const wroteFallback = fallbackStorage.writeSession(key, value);
        return wroteBrowser || wroteFallback;
    },
    readLocal(key: string): string | null {
        const value = readWebStorage(
            key,
            typeof localStorage === "undefined" ? undefined : localStorage,
        );
        return (value !== null ? value : fallbackStorage.readLocal(key));
    },
    writeLocal(key: string, value: string | null): boolean {
        const wroteBrowser = writeWebStorage(
            key,
            value,
            typeof localStorage === "undefined" ? undefined : localStorage,
        );
        const wroteFallback = fallbackStorage.writeLocal(key, value);
        return wroteBrowser || wroteFallback;
    },
};

export function readBrowserStorage(key: string): string | null {
    const localValue = browserStorageAdapter.readLocal(key);
    if (localValue !== null)
        return localValue;

    return browserStorageAdapter.readSession(key);
}

export function writeBrowserStorage(key: string, value: string | null): boolean {
    const wroteLocal = browserStorageAdapter.writeLocal(key, value);
    const wroteSession = browserStorageAdapter.writeSession(key, value);
    return wroteLocal || wroteSession;
}

export function readBrowserStorageJSON(key: string): object | null {
    const json = readBrowserStorage(key);
    if (!json)
        return null;

    try {
        const parsed = JSON.parse(json);
        if (typeof parsed !== "object") {
            console.warn(`Storage key "${key}" contains non-object JSON`);
            return null;
        }

        return parsed;
    } catch (error) {
        console.warn(`Failed to parse JSON from storage key "${key}"`, error);
        return null;
    }
}

export function writeBrowserStorageJSON(key: string, value: object): boolean {
    try {
        const json = JSON.stringify(value);
        return writeBrowserStorage(key, json);
    } catch (_) {
        return false;
    }
}
